import React from 'react';
import { Pressable, Text, StyleSheet, ViewStyle, Platform } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { createStyles } from '../../shared/utils/styles';
import { DesignTokens } from '../../shared/types/tokens';
import { Ionicons } from '@expo/vector-icons';
import { useResponsive, getResponsiveValue } from '../../shared/utils/responsive';
import { logger } from '../../shared/utils/logger';

export interface FloatingActionButtonProps {
  icon?: keyof typeof Ionicons.glyphMap;
  emoji?: string;
  onPress: () => void;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
  style?: ViewStyle;
  iconColor?: string;
}

export function FloatingActionButton({
  icon,
  emoji,
  onPress,
  position = 'bottom-right',
  size = 'md',
  variant = 'primary',
  disabled = false,
  style,
  iconColor,
}: FloatingActionButtonProps) {
  const { tokens } = useTheme();
  const utils = createStyles(tokens);
  const responsive = useResponsive();
  const styles = makeStyles(tokens, utils, responsive, size, position);

  if (!icon && !emoji) {
    logger.warn('FloatingActionButton requires either icon or emoji prop', 'ui');
    return null;
  }

  const defaultIconColor = variant === 'primary'
    ? tokens.color.onPrimary
    : variant === 'secondary'
      ? tokens.color.onSecondary
      : tokens.color.onError;

  const finalIconColor = iconColor || defaultIconColor;
  const iconSize = size === 'sm' ? 20 : size === 'md' ? 24 : 28;
  const emojiSize = size === 'sm' ? 20 : size === 'md' ? 24 : 28;

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={({ pressed }) => [
        styles.base,
        styles[variant],
        disabled && styles.disabled,
        pressed && !disabled && styles.pressed,
        style,
      ]}
    >
      {icon ? (
        <Ionicons 
          name={icon} 
          size={iconSize} 
          color={finalIconColor} 
        />
      ) : emoji ? (
        <Text style={[styles.emoji, { fontSize: emojiSize }]}>
          {emoji}
        </Text>
      ) : null}
    </Pressable>
  );
}

// Tab bar heights to ensure FAB doesn't overlap on mobile
const TAB_BAR_HEIGHT = {
  smallMobile: 50,
  mobile: 56,
  desktop: 64,
};

function makeStyles(
  tokens: DesignTokens,
  utils: ReturnType<typeof createStyles>,
  responsive: ReturnType<typeof useResponsive>,
  size: 'sm' | 'md' | 'lg',
  position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
) {
  const { isSmallMobile, isMobile } = responsive;
  
  // Smaller button on mobile
  const buttonSize = size === 'sm' 
    ? (isSmallMobile ? 40 : getResponsiveValue(44, 52, 60, 68))
    : size === 'md'
      ? (isSmallMobile ? 48 : getResponsiveValue(52, 60, 68, 76))
      : (isSmallMobile ? 56 : getResponsiveValue(60, 68, 76, 84));

  const horizontalOffset = isSmallMobile ? 12 : getResponsiveValue(16, 20, 24, 28);
  
  // Bottom offset needs to account for tab bar on mobile web
  const tabBarHeight = Platform.OS === 'web' 
    ? (isSmallMobile ? TAB_BAR_HEIGHT.smallMobile : isMobile ? TAB_BAR_HEIGHT.mobile : 0)
    : 0;
  const bottomOffset = (isSmallMobile ? 12 : getResponsiveValue(16, 20, 24, 28)) + tabBarHeight;
  const topOffset = isSmallMobile ? 12 : getResponsiveValue(16, 20, 24, 28);

  const positionStyles = {
    'bottom-right': {
      bottom: bottomOffset,
      right: horizontalOffset,
    },
    'bottom-left': {
      bottom: bottomOffset,
      left: horizontalOffset,
    },
    'top-right': {
      top: topOffset,
      right: horizontalOffset,
    },
    'top-left': {
      top: topOffset,
      left: horizontalOffset,
    },
  };

  return StyleSheet.create({
    base: {
      position: 'absolute',
      ...positionStyles[position],
      zIndex: 9999,
      width: buttonSize,
      height: buttonSize,
      borderRadius: buttonSize / 2,
      alignItems: 'center',
      justifyContent: 'center',
      ...Platform.select({
        web: {
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
          cursor: 'pointer',
        },
        default: {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 6 },
          shadowOpacity: 0.2,
          shadowRadius: 16,
          elevation: 10,
        },
      }),
    },
    primary: {
      backgroundColor: tokens.color.primary,
    },
    secondary: {
      backgroundColor: tokens.color.secondary,
    },
    danger: {
      backgroundColor: tokens.color.error,
    },
    emoji: {
      textAlign: 'center',
    },
    disabled: {
      opacity: 0.6,
    },
    pressed: {
      opacity: 0.8,
      transform: [{ scale: 0.95 }],
    },
  });
}

